import React, { useState, useEffect, ChangeEvent } from "react";
import { toast } from "react-toastify";

import { LabelInput } from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { FloatingLabelSelect } from "../../../../components/Reusable/Layout";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import {
  fetchDGCoinPlans,
  updateDGCoinPlan,
} from "../../../../features/auth/store/dgcoinPlanSlice";
import { fetchDGCoinProviders } from "../../../../features/auth/store/dgcoinProviderSlice";
import { fetchDGCoinCategories } from "../../../../features/auth/store/dgcoinCategorySlice";
import { fetchCountries } from "../../../../features/auth/store/countrySlice";
import { fetchCurrencies } from "../../../../features/auth/store/currencySlice";
import Modal from "../../../../components/Reusable/Modal";
import { DGCoinPlan } from "../../../../features/auth/store/types/dgcoin-types";

type EditPlanProps = {
  isOpen: boolean;
  onClose?: () => void;
  dgCoinPlan?: DGCoinPlan;
};

export const EditDGCoinModal: React.FC<EditPlanProps> = ({
  isOpen,
  onClose,
  dgCoinPlan,
}) => {
  const [formData, setFormData] = useState({
    provider_id: "",
    sub_category_id: "",
    name: "",
    message: "",
    code: "",
    wallet_price: "",
    api_price: "",
    atm_price: "",
    status: "on",
    source: "",
    countries: [] as string[],
    discount: "",
    price_type: "fixed",
    price_currency_id: "",
    index: 1,
  });

  const [image, setImage] = useState<File | undefined>(undefined);
  const [currentImage, setcurrentImage] = useState<string | undefined>(
    undefined
  );
  const [filteredSubCategories, setFilteredSubCategories] = useState<any[]>([]);
  const dispatch = useAppDispatch();
  const countries = useAppSelector((state) => state.countries?.items ?? []);
  const countriesLoading = useAppSelector(
    (state) => state.countries?.loading ?? false
  );
  const { dgcoinProviders } = useAppSelector((state) => state.dgcoinProviders);
  const { dgcoinCategories } = useAppSelector(
    (state) => state.dgcoinCategories
  );
  const currencies = useAppSelector((state) => state.currencies?.items ?? []);

  const deleteOldImage =
    dgCoinPlan?.image_url !== null && currentImage === undefined;

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchDGCoinProviders());
      dispatch(fetchCurrencies({ perPage: 200 }));
      dispatch(fetchCountries({ perPage: 200 }));
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (dgCoinPlan) {
      let countries = [] as string[];
      if (dgCoinPlan.countries) {
        const isString = dgCoinPlan.countries.every(
          (country) => typeof country === "string"
        );
        countries = isString
          ? (dgCoinPlan.countries as string[])
          : dgCoinPlan.countries.map((country) => String(country));
      }

      setcurrentImage(dgCoinPlan.image_url);
      setFormData({
        provider_id: dgCoinPlan.category.provider_id?.toString(),
        sub_category_id: dgCoinPlan.category_id?.toString(),
        wallet_price: dgCoinPlan.wallet_price?.toString() || "",
        api_price: dgCoinPlan?.api_price?.toString() || "",
        atm_price: dgCoinPlan?.atm_price?.toString() || "",
        discount: dgCoinPlan.discount?.toString() || "",
        status: dgCoinPlan.status,
        source: dgCoinPlan.source,
        code: dgCoinPlan.code || "",
        price_type: dgCoinPlan.price_type || "fixed",
        price_currency_id: dgCoinPlan.price_currency_id?.toString() || "",
        name: dgCoinPlan.name || "",
        message: dgCoinPlan.message,
        index: dgCoinPlan.index || 1,
        countries,
      });
    }
  }, [dgCoinPlan]);

  useEffect(() => {
    if (formData.provider_id) {
      dispatch(fetchDGCoinCategories({}));
    }
  }, [dispatch, formData.provider_id]);

  useEffect(() => {
    if (formData.provider_id && dgcoinCategories.length) {
      setFilteredSubCategories(
        dgcoinCategories.filter(
          (cat) => String(cat.provider_id) === String(formData.provider_id)
        )
      );
    } else {
      setFilteredSubCategories([]);
    }
  }, [formData.provider_id, dgcoinCategories]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "provider_id" ? { sub_category_id: "" } : {}),
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const removeImage = () => {
    setImage(undefined);
  };

  const toggleCountry = (id: number) => {
    setFormData((prev) => {
      const exists = prev.countries.includes(String(id));
      return {
        ...prev,
        countries: exists
          ? prev.countries.filter((c) => c !== String(id))
          : [...prev.countries, String(id)],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formDataObj = new FormData();
    formDataObj.append("provider_id", formData.provider_id);
    formDataObj.append("sub_category_id", formData.sub_category_id);
    formDataObj.append("category_id", formData.sub_category_id);
    formDataObj.append("name", formData.name);
    formDataObj.append("message", formData.message || "");
    formDataObj.append("wallet_price", formData.wallet_price || "0");
    formDataObj.append("api_price", formData.api_price || "0");
    formDataObj.append("atm_price", formData.atm_price || "0");
    formDataObj.append("status", formData.status);
    formDataObj.append("source", formData.source);
    formDataObj.append("discount", formData.discount);
    formDataObj.append("code", formData.code || "");
    formDataObj.append(
      "countries",
      JSON.stringify(formData.countries.map((c) => Number(c)))
    );
    formDataObj.append("price_type", formData.price_type || "fixed");
    formDataObj.append("price_currency_id", formData.price_currency_id);
    formDataObj.append("index", String(formData.index || 1));

    if (image) {
      formDataObj.append("image", image);
    }
    if (deleteOldImage) {
      formDataObj.append("deleteOldImage", deleteOldImage.toString());
    }
    const filters: any = {};
    try {
      await dispatch(
        updateDGCoinPlan({ data: formDataObj, id: dgCoinPlan?.id as number })
      ).unwrap();
      toast.success("DG Coin plan updated successfully!");
      handleClose();
      await dispatch(fetchDGCoinPlans(filters));
    } catch (error: any) {
      toast.error(error?.message || "Failed to update DG Coin plan");
      console.error("Submission error:", error);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    setFormData({
      provider_id: "",
      sub_category_id: "",
      name: "",
      message: "",
      wallet_price: "",
      api_price: "",
      atm_price: "",
      status: "on",
      source: "",
      countries: [],
      code: "",
      discount: "",
      price_type: "fixed",
      price_currency_id: "",
      index: 1,
    });
    setcurrentImage(undefined);
    setImage(undefined);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-white p-6 w-[400px] max-h-[90vh] overflow-y-auto  dark:bg-black dark:text-white">
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-base font-semibold">DG Coin Plan Details</p>
          <FloatingLabelSelect
            name="provider_id"
            placeholder="Provider"
            value={formData.provider_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Provider" },
              ...dgcoinProviders.map((p) => ({
                value: String(p.id),
                label: p.name,
              })),
            ]}
          />
          <FloatingLabelSelect
            name="sub_category_id"
            placeholder="Sub Category"
            value={formData.sub_category_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Sub Category" },
              ...filteredSubCategories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              })),
            ]}
            disabled={!formData.provider_id}
          />
          <div className="space-y-2">
            <FloatingLabelSelect
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              placeholder="Status"
              options={[
                { value: "on", label: "Active" },
                { value: "off", label: "Inactive" },
              ]}
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="name"
              placeholder="Name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="message"
              placeholder="Message"
              value={formData.message}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="code"
              placeholder="Code"
              value={formData.code}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="wallet_price"
              placeholder="Wallet Price"
              value={formData.wallet_price}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="api_price"
              placeholder="API Price"
              value={formData.api_price}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="atm_price"
              placeholder="ATM Price"
              value={formData.atm_price}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="discount"
              placeholder="Discount"
              value={formData.discount}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="source"
              placeholder="Source"
              value={formData.source}
              onChange={handleInputChange}
              required
            />
          </div>
          <FloatingLabelSelect
            name="price_currency_id"
            placeholder="Currency"
            value={formData.price_currency_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Currency" },
              ...currencies.map((c) => ({
                value: String(c.id),
                label: `${c.name} (${c.symbol})`,
              })),
            ]}
            disabled={!currencies?.length}
          />
          <FloatingLabelSelect
            name="price_type"
            placeholder="Price Type"
            value={formData.price_type}
            onChange={handleInputChange}
            options={[
              { value: "fixed", label: "Fixed" },
              { value: "percent", label: "Percent" },
            ]}
          />
          <div className="space-y-2">
            <LabelInput
              type="number"
              name="index"
              placeholder="Index"
              value={String(formData.index)}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-semibold">Countries</p>
            <div className="max-h-28 overflow-y-auto border border-gray-200 rounded p-2">
              {countriesLoading ? (
                <p className="text-sm text-gray-500">Loading countries...</p>
              ) : (
                countries.map((country: any) => (
                  <label
                    key={country.id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={formData.countries.includes(String(country.id))}
                      onChange={() => toggleCountry(country.id)}
                    />
                    {country.name}
                  </label>
                ))
              )}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm text-gray-500">Image (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500"
            />
            {image && (
              <div className="flex items-center gap-2">
                <span className="text-xs">{image.name}</span>
                <button
                  type="button"
                  className="text-red-500 text-xs"
                  onClick={removeImage}
                >
                  Remove
                </button>
              </div>
            )}
            {currentImage && (
              <div>
                <p className="text-base font-semibold">Current Product Image</p>
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="relative rounded-md overflow-hidden h-20">
                    <img
                      src={currentImage}
                      alt="product"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setcurrentImage(undefined)}
                      className="absolute top-1 right-1 bg-white rounded-full p-1"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 6L18 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              onClick={handleClose}
              bgColor="bg-gray-200"
              textColor="text-gray-700"
              borderRadius="rounded-lg"
              className="px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              textColor="text-white"
              borderRadius="rounded-lg"
              className="px-4 py-2"
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
